import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { GoodsReceiptEntry } from '../../models/inventory/goods-receipt';
import { GoodsReceiptSearchCommand } from '../../models/inventory/goods-receipt-search';
import { PurchaseOrderReceivedQuantity } from '../../models/inventory/purchase-order-received-quantity';
import { ApiResponse, EntityKeyByReference } from '@card-software/library';
import { getSkipperErrorHeader } from '../../utils/error-header.util';

@Injectable({
  providedIn: 'root',
})
export class GoodsReceiptProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.inventoryApi + '/api/v1/goods-receipt';

  public getByEntityKey(entityKey: EntityKeyByReference, skipInterceptor = false): Observable<ApiResponse<GoodsReceiptEntry>> {
    const headers = getSkipperErrorHeader(skipInterceptor);
    return this.httpClient.post<ApiResponse<GoodsReceiptEntry>>(this.baseUrl + '/entity-key', entityKey, { headers });
  }

  /**
   * Crea un nuevo GoodsReceipt desde un Purchase Order
   * @param entityKey - EntityKey del Purchase Order
   * @param locationCode - Código de ubicación donde se recibirá
   */
  public createFromEntityKey(
    entityKey: EntityKeyByReference,
    locationCode: string,
    skipInterceptor = false,
  ): Observable<ApiResponse<GoodsReceiptEntry>> {
    const headers = getSkipperErrorHeader(skipInterceptor);
    return this.httpClient.post<ApiResponse<GoodsReceiptEntry>>(
      `${this.baseUrl}/entity-key?locationCode=${encodeURIComponent(locationCode)}`,
      entityKey,
      {
        headers,
      },
    );
  }

  public search(searchCommand: GoodsReceiptSearchCommand, skipInterceptor = false): Observable<ApiResponse<GoodsReceiptEntry[]>> {
    const headers = getSkipperErrorHeader(skipInterceptor);
    return this.httpClient.post<ApiResponse<GoodsReceiptEntry[]>>(this.baseUrl + '/search', searchCommand, { headers });
  }

  public getById(id: string, skipInterceptor = false): Observable<ApiResponse<GoodsReceiptEntry>> {
    const headers = getSkipperErrorHeader(skipInterceptor);
    return this.httpClient.get<ApiResponse<GoodsReceiptEntry>>(`${this.baseUrl}/${id}`, { headers });
  }

  /**
   * Cantidades netas recibidas por item de la Purchase Order:
   * Goods Receipts completados menos Provider Returns completados.
   */
  public getReceivedQuantitiesByPurchaseOrder(purchaseOrderId: string): Observable<ApiResponse<PurchaseOrderReceivedQuantity[]>> {
    return this.httpClient.get<ApiResponse<PurchaseOrderReceivedQuantity[]>>(
      `${this.baseUrl}/purchase-order/${purchaseOrderId}/received-quantities`,
    );
  }

  public create(body: GoodsReceiptEntry): Observable<ApiResponse<GoodsReceiptEntry>> {
    return this.httpClient.post<ApiResponse<GoodsReceiptEntry>>(this.baseUrl, body);
  }

  public update(id: string, body: GoodsReceiptEntry): Observable<ApiResponse<GoodsReceiptEntry>> {
    return this.httpClient.put<ApiResponse<GoodsReceiptEntry>>(`${this.baseUrl}/${id}`, body);
  }

  /**
   * Actualiza solo el estado del GoodsReceipt
   * @param id - ID del GoodsReceipt
   * @param newStatus - Nuevo estado
   */
  public updateStatus(id: string, newStatus: number): Observable<ApiResponse<GoodsReceiptEntry>> {
    return this.httpClient.put<ApiResponse<GoodsReceiptEntry>>(`${this.baseUrl}/status/${id}`, newStatus);
  }

  public delete(id: string): Observable<ApiResponse<void>> {
    return this.httpClient.delete<ApiResponse<void>>(`${this.baseUrl}/${id}`);
  }

  /**
   * Descarga el reporte PDF del GoodsReceipt
   * @param id - ID del GoodsReceipt
   */
  public downloadReport(id: string): Observable<Blob> {
    return this.httpClient.get(`${this.baseUrl}/report/${id}`, { responseType: 'blob' });
  }
}
